import React, { useState, useEffect } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Paper, IconButton, InputBase } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Clock01Icon, Search01Icon, TradeDownIcon, TradeUpIcon } from '@hugeicons/core-free-icons';
import Card from './Card';
import ExpensePieChart from './ExpensePieChart';

const Dashboard = ({ searchTerm, setSearchTerm }) => {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState('recent');
  const [expenseData, setExpenseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExpenseData = async () => {
      try {
        const response = await fetch('http://127.0.0.1:8000/dashboard/recent-tracker');
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setExpenseData(data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchExpenseData();
  }, []);

  const handleToggle = (event, newOption) => {
    if (newOption !== null) setSelectedOption(newOption);
  };
  const handleAddNewDiaryClick = () => {
    navigate('/diary');
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Box sx={{ mt: 10, px: 2 }}>
      {/* Header with title and search */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "space-between", flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500, color: '#545860' }}>Recent Diaries</Typography>
          <Paper
            component="form"
            sx={{
              p: "4px 8px",
              display: "flex",
              alignItems: "center",
              borderRadius: 100,
              width: { xs: 180, sm: 230 },
              boxShadow: 'none',
              bgcolor: '#fff',
            }}
          >
            <IconButton
              size="small"
              sx={{
                bgcolor: "#fff",
                borderRadius: "50%",
                p: 0.5,
                mr: 1,
              }}
            >
              <HugeiconsIcon icon={Search01Icon} size={20} />
            </IconButton>
            <InputBase
              placeholder="Search..."
              inputProps={{ "aria-label": "search" }}
              sx={{ fontSize: 14, flex: 1 }}
            />
          </Paper>
        </Box>
        {/* Toggle sort buttons */}
        <Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedOption}
            onChange={handleToggle}
            sx={{
              backgroundColor: "#ffffffff",
              borderRadius: "999px",
              p: "2px",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                px: 2,
                color: "#6b7280",
                border: "none",
                borderRadius: 0,
                backgroundColor: "transparent",
              },
              "& .MuiToggleButton-root:first-of-type.Mui-selected": {
                color: "#1976d2",
                backgroundColor: "#e6f0ff",
                border: "1px solid #1976d2",
                borderTopLeftRadius: "999px",
                borderBottomLeftRadius: "999px",
                zIndex: 1,
              },
              "& .MuiToggleButton-root:first-of-type:not(.Mui-selected)": {
                borderRight: "1px solid #d1d5db",
              },
              "& .MuiToggleButton-root:nth-of-type(2).Mui-selected": {
                color: "#1976d2",
                backgroundColor: "#e6f0ff",
                border: "1px solid #1976d2",
                zIndex: 1,
              },
              "& .MuiToggleButton-root:last-of-type.Mui-selected": {
                color: "#1976d2",
                backgroundColor: "#e6f0ff",
                border: "1px solid #1976d2",
                borderTopRightRadius: "999px",
                borderBottomRightRadius: "999px",
                zIndex: 1,
              },
              "& .MuiToggleButton-root:last-of-type:not(.Mui-selected)": {
                borderLeft: "1px solid #d1d5db",
              },
            }}
          >
            <ToggleButton value="low">
              Low{" "}
              <HugeiconsIcon
                icon={TradeDownIcon}
                size={18}
                style={{ marginLeft: 6, color: "red" }}
              />
            </ToggleButton>
            <ToggleButton value="recent">
              Recent{" "}
              <HugeiconsIcon
                icon={Clock01Icon}
                size={18}
                style={{ marginLeft: 6 }}
              />
            </ToggleButton>
            <ToggleButton value="high">
              High{" "}
              <HugeiconsIcon
                icon={TradeUpIcon}
                size={18}
                style={{ marginLeft: 6, color: "green" }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Cards Section */}
      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
        {expenseData.map((item) => (
          <Card
            key={item.id}
            category={item.category}
            title={item.name}
            amount={item.amounts}
            image={item.category}
            change={item.change}
          />
        ))}
        {/* Add New Diary card */}
        <Box
          onClick={handleAddNewDiaryClick}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed #ccc',
            borderRadius: 2,
            width: 280,
            height: 130,
            color: '#85878C',
            fontFamily: 'Poppins',
            fontSize: 16,
            fontWeight: 500,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: '#f0f0f0',
              color: '#0056FF',
            },
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography sx={{ opacity: 0 }}>+</Typography>
          <Typography className='Add'>Add New Record</Typography>
        </Box>
      </Box>

      {/* Pie Chart Section */}
      <Box sx={{ mt: 4, px: 4 }}>
        <ExpensePieChart />
      </Box>
    </Box>
  );
};

export default Dashboard;
